import { Camera, RefreshCw, Trash2 } from 'lucide-react';
import { useId, type CSSProperties } from 'react';
import { cx } from '../../utils/cx';
import { Icon } from '../Icon/Icon';
import s from './ImageInput.module.css';

export type ImageInputProps = {
  /** Current image URL (a local object URL while uploading, or the saved image). */
  src?: string | null;
  onFile: (file: File) => void;
  onRemove?: () => void;
  /** 0–1 while uploading; undefined when idle. */
  progress?: number;
  error?: string | null;
  label?: string;
  disabled?: boolean;
  className?: string;
};

/**
 * A tappable photo slot. Knows nothing about uploads: it hands a File to `onFile`
 * (the app's PhotoField resizes/uploads it and feeds back `src` + `progress`).
 */
export function ImageInput({
  src,
  onFile,
  onRemove,
  progress,
  error,
  label = 'Add photo',
  disabled,
  className,
}: ImageInputProps) {
  const id = useId();
  const uploading = progress !== undefined && progress < 1;
  return (
    <div className={cx(s.wrap, className)}>
      <label
        htmlFor={id}
        className={s.slot}
        data-has-image={src ? true : undefined}
        data-disabled={disabled || undefined}
      >
        {src ? <img src={src} alt="" className={s.image} /> : <Icon icon={Camera} size="lg" />}
        {uploading ? (
          <span
            className={s.progress}
            style={{ '--p': `${Math.round((progress ?? 0) * 100)}%` } as CSSProperties}
            role="progressbar"
            aria-valuenow={Math.round((progress ?? 0) * 100)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Uploading photo"
          />
        ) : null}
        <input
          id={id}
          type="file"
          accept="image/*"
          disabled={disabled || uploading}
          className={s.input}
          aria-label={src ? 'Replace photo' : label}
          onChange={(e) => {
            const file = e.currentTarget.files?.[0];
            if (file) onFile(file);
            e.currentTarget.value = '';
          }}
        />
      </label>
      <div className={s.actions}>
        {src ? (
          <>
            <label htmlFor={id} className={s.action}>
              <Icon icon={RefreshCw} size="sm" /> Replace
            </label>
            {onRemove ? (
              <button type="button" className={s.action} onClick={onRemove} disabled={uploading}>
                <Icon icon={Trash2} size="sm" /> Remove
              </button>
            ) : null}
          </>
        ) : (
          <label htmlFor={id} className={s.action}>
            {label}
          </label>
        )}
      </div>
      {error ? (
        <p className={s.error} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
